import { SelectionModel } from '@angular/cdk/collections';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FlexModule } from '@angular/flex-layout/flex';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MonthlyFacilityFeeCharge, MonthlyFacilityFeeChargeSortType } from '@portal-workspace/grow-shared-library';
import { CustomContentLoaderComponent, CustomPaginatorComponent, PortalHotToastService } from '@portal-workspace/grow-ui-library';
import { createAsyncStore, loadingFor } from '@ngneat/loadoff';
import moment from 'moment';
import { BankingService } from '../../../service/banking.service';

@Component({
    selector: 'monthly-fee-list',
    templateUrl: './monthly-fee-list.component.html',
    styleUrls: ['./monthly-fee-list.component.scss'],
    standalone: true,
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        CommonModule,
        MatTableModule,
        MatFormFieldModule,
        MatSelectModule,
        MatSortModule,
        MatInputModule,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatMenuModule,
        MatCheckboxModule,
        MatTooltipModule,
        FormsModule,
        CustomContentLoaderComponent,
        CustomPaginatorComponent,
        FlexModule,
        NgClass,
        AsyncPipe,
    ],
    providers: [BankingService]
})
export class MonthlyFeeListComponent implements OnInit {
    displayedColumns: string[] = [
        'select',
        'id',
        'pismoAccountId',
        'periodYearMonth',
        'computedAmount',
        'chargedAmount',
        'pismoTrackingId',
        'status',
        'attemptCount',
        'createdDate',
        'lastAttemptDate',
        'errorMessage',
        'actions'
    ];

    dataSource: MonthlyFacilityFeeCharge[] = [];
    total = 0;
    limit = 20;
    offset = 0;
    sorts: MonthlyFacilityFeeChargeSortType = [{ prop: 'createdDate', dir: 'DESC' }];
    sortActive = 'createdDate';
    sortDirection: 'asc' | 'desc' | '' = 'desc';
    expandedElementId: number | null = null;
    selection = new SelectionModel<MonthlyFacilityFeeCharge>(true, []);
    moment = moment;
    loader = loadingFor('tableLoading');
    store = createAsyncStore();

    statusFilter = 'all';
    accountIdFilter = '';
    periodYearMonthFilter = '';
    periodDate: Date | null = null;
    startDate: Date | null = null;
    endDate: Date | null = null;

    statusOptions = [
        { value: 'all', label: 'All' },
        { value: 'pending', label: 'Pending' },
        { value: 'processing', label: 'Processing' },
        { value: 'success', label: 'Success' },
        { value: 'failed', label: 'Failed' },
        { value: 'skipped', label: 'Skipped' }
    ];

    isLoading = false;
    hasFilters = false;

    constructor(
        private bankingService: BankingService,
        private toastService: PortalHotToastService
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        this.isLoading = true;
        this.selection.clear();

        const opt = {
            page: {
                limit: this.limit,
                offset: this.offset,
            },
            filter: '',
            sorts: this.sorts,
            statusFilter: this.statusFilter !== 'all' ? this.statusFilter : undefined,
            accountId: this.accountIdFilter ? Number(this.accountIdFilter) : undefined,
            periodYearMonth: this.periodYearMonthFilter || undefined,
            startDate: this.startDate ? moment(this.startDate).format('YYYY-MM-DD') : null,
            endDate: this.endDate ? moment(this.endDate).format('YYYY-MM-DD') : null,
        };

        this.updateFiltersStatus();

        this.bankingService.getMonthlyFacilityFeeChargesFn(opt).subscribe({
            next: (response) => {
                this.dataSource = response.payload;
                this.total = response.total;
                this.isLoading = false;
            },
            error: () => {
                this.toastService.error('Error loading monthly fee charges');
                this.isLoading = false;
            }
        });
    }

    onSortData(event: Sort): void {
        this.sortActive = event.active;
        this.sortDirection = event.direction;
        this.sorts = event.direction && event.active
            ? [{ prop: event.active as any, dir: event.direction }]
            : [{ prop: 'createdDate', dir: 'DESC' }];
        this.offset = 0;
        this.loadData();
    }

    onPagination(event: PageEvent): void {
        this.offset = event.pageIndex;
        this.limit = event.pageSize;
        this.loadData();
    }

    applyFilters(): void {
        this.offset = 0;
        this.loadData();
    }

    clearFilters(): void {
        this.statusFilter = 'all';
        this.accountIdFilter = '';
        this.periodYearMonthFilter = '';
        this.periodDate = null;
        this.startDate = null;
        this.endDate = null;
        this.offset = 0;
        this.loadData();
    }

    updateFiltersStatus(): void {
        const hasStatusFilter = this.statusFilter !== 'all';
        const hasAccountIdFilter = this.accountIdFilter.trim() !== '';
        const hasPeriodFilter = this.periodYearMonthFilter.trim() !== '';
        const hasDateFilter = this.startDate !== null || this.endDate !== null;

        this.hasFilters = hasStatusFilter || hasAccountIdFilter || hasPeriodFilter || hasDateFilter;
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'success':
                return 'status-success';
            case 'failed':
                return 'status-failed';
            case 'pending':
                return 'status-pending';
            case 'processing':
                return 'status-processing';
            case 'skipped':
                return 'status-skipped';
            default:
                return '';
        }
    }

    formatDate(dateString: string | null): string {
        if (!dateString) return '-';
        return moment(dateString).format('DD/MM/YYYY HH:mm');
    }

    formatAmount(amount: number | null): string {
        if (amount === null || amount === undefined) return '-';
        return `$${amount.toFixed(2)}`;
    }

    setPeriodMonth(normalizedMonth: Date, datepicker: MatDatepicker<Date>): void {
        this.periodDate = normalizedMonth;
        this.periodYearMonthFilter = moment(normalizedMonth).format('YYYY-MM');
        datepicker.close();
    }

    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.length;
        return numSelected === numRows;
    }

    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.dataSource.forEach(row => this.selection.select(row));
        }
    }

    toggleRow(row: MonthlyFacilityFeeCharge) {
        this.selection.toggle(row);
    }

    showRetryButton(charge: MonthlyFacilityFeeCharge): boolean {
        return charge.status === 'failed';
    }

    retryCharge(charge: MonthlyFacilityFeeCharge): void {
        this.retryCharges([charge.id]);
    }

    retrySelected(): void {
        const selectedIds = this.selection.selected.map(charge => charge.id);
        if (selectedIds.length === 0) {
            this.toastService.error('Please select at least one charge to retry');
            return;
        }

        const hasNonFailedCharge = this.selection.selected.some(charge => charge.status !== 'failed');
        if (hasNonFailedCharge) {
            this.toastService.error('Only failed monthly fee charges can be retried');
            return;
        }

        this.retryCharges(selectedIds);
    }

    private retryCharges(chargeIds: number[]): void {
        this.bankingService.retryMonthlyFacilityFeeChargesFn(chargeIds).pipe(
            this.toastService.spinnerObservable(),
        ).subscribe({
            next: (response) => {
                if (response.status) {
                    this.toastService.success(response.message || 'Monthly fee charges retried successfully');
                    this.loadData();
                } else {
                    this.toastService.error(response.message || 'Failed to retry monthly fee charges');
                }
            },
            error: () => {
                this.toastService.error('Error retrying monthly fee charges');
            }
        });
    }
}
